import { ensureId } from '../floating.js';

const registry = new Map();
let scrollLocks = 0;

function lockScroll(lock) {
    const html = document.documentElement;
    if (lock && scrollLocks === 0) {
        // Compensate for the disappearing scrollbar so the page doesn't shift.
        html.style.setProperty('--mellow-scrollbar-width', `${window.innerWidth - html.clientWidth}px`);
    }
    scrollLocks = Math.max(0, scrollLocks + (lock ? 1 : -1));
    html.toggleAttribute('data-mellow-scroll-locked', scrollLocks > 0);
}

/**
 * <mellow-modal name="…">: wraps a native <dialog> (focus trap, inert background, top layer).
 *
 * - `.value` is the open state, and it fires `input` when it changes, so `wire:model` works.
 * - Open/close from anywhere: Mellow.modal('name').show(), [data-mellow-modal-trigger="name"],
 *   or the `mellow-modal` window event (used by Mellow::modal() in Livewire).
 */
export class MellowModal extends HTMLElement {
    connectedCallback() {
        const name = this.getAttribute('name');
        if (name) registry.set(name, this);
        if (this._ready) return;
        this._ready = true;

        const dialog = this.dialog;
        if (!dialog) return;

        this.sync();

        dialog.addEventListener('cancel', (event) => {
            if (!this.dismissible) event.preventDefault();
        });

        dialog.addEventListener('close', () => {
            lockScroll(false);
            this.emit();
            this.dispatchEvent(new CustomEvent('mellow:close', { bubbles: true }));
            this._returnFocus?.focus?.();
        });

        // Clicking the backdrop closes the dialog – judged by position: clicks on empty space
        // inside the dialog (its padding) also target the <dialog> element itself.
        dialog.addEventListener('pointerdown', (event) => (this._downOnBackdrop = this.isOutside(event)));
        dialog.addEventListener('click', (event) => {
            if (!this._downOnBackdrop || !this.isOutside(event)) return;
            if (this.dismissible && !this.persistent) this.close();
            else this.nudge();
        });

        if (this.hasAttribute('open')) this.show();
    }

    disconnectedCallback() {
        if (registry.get(this.getAttribute('name')) === this) registry.delete(this.getAttribute('name'));
        if (this.dialog?.open) lockScroll(false);
    }

    /**
     * Label the dialog with its first heading unless labelled explicitly. Runs again after
     * Livewire updates, because morphing removes the client-generated heading id.
     */
    sync() {
        const dialog = this.dialog;
        if (!dialog || dialog.hasAttribute('aria-label')) return;

        const labelledBy = dialog.getAttribute('aria-labelledby');
        if (labelledBy && !dialog.hasAttribute('data-mellow-auto-label')) return;
        if (labelledBy && document.getElementById(labelledBy)) return;

        const heading = dialog.querySelector('h1, h2, h3, h4, .mellow-heading');
        if (heading) {
            dialog.setAttribute('aria-labelledby', ensureId(heading, 'mellow-modal-heading'));
            dialog.setAttribute('data-mellow-auto-label', '');
        }
    }

    get dialog() {
        return this.querySelector(':scope > dialog');
    }

    get dismissible() {
        return this.getAttribute('dismissible') !== 'false';
    }

    /** Stays open on outside clicks; Escape and the close button still work. */
    get persistent() {
        return this.hasAttribute('persistent');
    }

    isOutside(event) {
        if (event.target !== this.dialog) return false;
        const rect = this.dialog.getBoundingClientRect();
        return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    }

    /** A short bounce: "I'm staying open" feedback for a blocked outside click. */
    nudge() {
        const dialog = this.dialog;
        dialog.removeAttribute('data-nudge');
        void dialog.offsetWidth; // restart the animation
        dialog.setAttribute('data-nudge', '');
        dialog.addEventListener('animationend', () => dialog.removeAttribute('data-nudge'), { once: true });
    }

    get value() {
        return Boolean(this.dialog?.open);
    }

    set value(open) {
        open ? this.show() : this.close();
    }

    show() {
        const dialog = this.dialog;
        if (!dialog || dialog.open) return;

        this._returnFocus = document.activeElement;
        dialog.showModal();
        lockScroll(true);
        this.emit();
        this.dispatchEvent(new CustomEvent('mellow:open', { bubbles: true }));
    }

    close() {
        if (this.dialog?.open) this.dialog.close();
    }

    toggle() {
        this.value = !this.value;
    }

    emit() {
        this.dispatchEvent(new CustomEvent('input', { bubbles: true, detail: this.value }));
    }
}

export function modal(name) {
    const find = () => registry.get(name) ?? document.querySelector(`mellow-modal[name="${CSS.escape(name)}"]`);
    return {
        show: () => find()?.show(),
        close: () => find()?.close(),
        toggle: () => find()?.toggle(),
        get open() {
            return find()?.value ?? false;
        },
    };
}

export function closeAllModals() {
    document.querySelectorAll('mellow-modal').forEach((el) => el.close());
}

export function registerModalBehaviors() {
    document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-mellow-modal-trigger]');
        if (trigger) {
            modal(trigger.dataset.mellowModalTrigger).show();
            return;
        }

        const closer = event.target.closest('[data-mellow-modal-close]');
        if (closer) closer.closest('mellow-modal')?.close();
    });

    // Livewire: Mellow::modal('name')->show() / ->close(), Mellow::closeModals()
    window.addEventListener('mellow-modal', (event) => {
        const { name, action = 'show' } = event.detail ?? {};
        if (!name) return action === 'close' ? closeAllModals() : null;
        modal(name)[action]?.();
    });
}
